<template>
  <div class="main-certificate-page">
    <!-- ===== Hero / Summary Header ===== -->
    <b-card
      no-body
      class="hero-card mb-1 overflow-hidden"
    >
      <div class="hero-inner">
        <div class="hero-text">
          <div class="hero-badge">
            <feather-icon
              icon="AwardIcon"
              size="18"
            />
            <span>Certificates Directory</span>
          </div>
          <h2 class="hero-title">
            Main Certificates
          </h2>
          <p class="hero-subtitle">
            Browse all certification programs grouped by their issuing source, and
            explore the engineers assigned to each certificate.
          </p>
        </div>

        <div class="hero-stats">
          <div class="stat-pill">
            <div class="stat-icon stat-icon--primary">
              <feather-icon
                icon="FileTextIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ totalCertificates }}
              </div>
              <div class="stat-label">
                Certificates
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--info">
              <feather-icon
                icon="LayersIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ sourcesCount }}
              </div>
              <div class="stat-label">
                Sources
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--success">
              <feather-icon
                icon="UsersIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ uniqueEngineersCount }}
              </div>
              <div class="stat-label">
                Engineers
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- ===== Toolbar : Search + Source filters ===== -->
    <b-card
      no-body
      class="toolbar-card mb-1"
    >
      <div class="toolbar-inner">
        <b-input-group class="search-group">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="search"
            placeholder="Search by certificate name or engineer..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="search"
            is-text
            class="clear-search"
            @click.native="search = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>

        <div class="source-chips">
          <button
            type="button"
            class="source-chip"
            :class="{ 'source-chip--active': activeSource === 'all' }"
            @click="activeSource = 'all'"
          >
            <feather-icon
              icon="GridIcon"
              size="14"
            />
            <span>All</span>
          </button>
          <button
            v-for="src in availableSources"
            :key="src"
            type="button"
            class="source-chip"
            :class="{ 'source-chip--active': activeSource === src }"
            :style="activeSource === src ? chipActiveStyle(src) : {}"
            @click="activeSource = src"
          >
            <feather-icon
              :icon="sourceMeta(src).icon"
              size="14"
            />
            <span>{{ src }}</span>
          </button>
        </div>
      </div>
    </b-card>

    <!-- ===== Loading ===== -->
    <div
      v-if="dataLoad"
      class="text-center my-5 py-5"
    >
      <b-spinner
        type="grow"
        variant="primary"
        label="Loading..."
      />
      <p class="mt-1 text-muted">
        Loading certificates...
      </p>
    </div>

    <!-- ===== Empty state ===== -->
    <b-card
      v-else-if="!hasResults"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No certificates found</h4>
      <p class="text-muted mb-0">
        Try adjusting your search or filter.
      </p>
    </b-card>

    <!-- ===== Content : grouped by source ===== -->
    <div
      v-else
      class="groups-wrapper"
    >
      <div
        v-for="(items, source) in filteredGroups"
        :key="source"
        class="source-group"
      >
        <!-- Group header -->
        <div
          class="group-header"
          :style="{ background: sourceMeta(source).gradient }"
        >
          <div class="group-header-left">
            <div class="group-header-icon">
              <feather-icon
                :icon="sourceMeta(source).icon"
                size="22"
              />
            </div>
            <div>
              <h3 class="group-title">
                {{ source }}
              </h3>
              <span class="group-sub">Issuing Source</span>
            </div>
          </div>
          <b-badge
            pill
            class="group-count"
          >
            {{ items.length }} {{ items.length === 1 ? 'certificate' : 'certificates' }}
          </b-badge>
        </div>

        <!-- Cards -->
        <b-row class="mt-50">
          <b-col
            v-for="item in items"
            :key="item.id"
            cols="12"
            md="6"
            xl="4"
          >
            <div class="cert-card">
              <div
                class="cert-accent"
                :style="{ background: sourceMeta(source).gradient }"
              />

              <div class="cert-body">
                <div class="cert-top">
                  <div
                    class="cert-source-icon"
                    :style="{ background: sourceMeta(source).soft, color: sourceMeta(source).color }"
                  >
                    <feather-icon
                      :icon="sourceMeta(source).icon"
                      size="20"
                    />
                  </div>
                  <b-badge
                    variant="light-secondary"
                    class="cert-id"
                  >
                    #{{ item.id }}
                  </b-badge>
                </div>

                <h4
                  class="cert-name-en"
                  :title="item.name && item.name.en"
                >
                  {{ (item.name && item.name.en) || '—' }}
                </h4>
                <p
                  class="cert-name-ar"
                  dir="rtl"
                >
                  {{ (item.name && item.name.ar) || '—' }}
                </p>

                <!-- Engineers -->
                <div class="cert-engineers">
                  <div class="cert-engineers-head">
                    <feather-icon
                      icon="UsersIcon"
                      size="14"
                      class="mr-25"
                    />
                    <span>Engineers</span>
                    <b-badge
                      pill
                      variant="light-primary"
                      class="ml-50"
                    >
                      {{ engineerList(item.engineer_names).length }}
                    </b-badge>
                  </div>

                  <div
                    v-if="engineerList(item.engineer_names).length"
                    class="avatar-stack"
                    @click="ApprovalRow(item.engineer_names, item.name && item.name.en)"
                  >
                    <div
                      v-for="(name, idx) in engineerList(item.engineer_names).slice(0, 5)"
                      :key="idx"
                      class="avatar-chip"
                      :style="{ background: avatarColor(name) }"
                      :title="name"
                    >
                      {{ initials(name) }}
                    </div>
                    <div
                      v-if="engineerList(item.engineer_names).length > 5"
                      class="avatar-chip avatar-more"
                    >
                      +{{ engineerList(item.engineer_names).length - 5 }}
                    </div>
                  </div>
                  <div
                    v-else
                    class="no-engineers"
                  >
                    <feather-icon
                      icon="UserXIcon"
                      size="14"
                      class="mr-25"
                    />
                    No engineers assigned
                  </div>
                </div>
              </div>

              <div class="cert-footer">
                <button
                  type="button"
                  class="footer-btn footer-btn--ghost"
                  :disabled="!engineerList(item.engineer_names).length"
                  :style="{
                    color: sourceMeta(source).color,
                    borderColor: sourceMeta(source).color,
                  }"
                  @click="ApprovalRow(item.engineer_names, item.name && item.name.en)"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="15"
                    class="mr-25"
                  />
                  Engineers
                </button>
                <button
                  type="button"
                  class="footer-btn footer-btn--solid"
                  :style="{ background: sourceMeta(source).gradient }"
                  @click="$router.push({
                    name: 'certificate',
                    params: { MCR_id: item.id, Certificate_Name: item.name.en }
                  })"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="15"
                    class="mr-25"
                  />
                  View Details
                </button>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BSpinner, BBadge,
  BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import DOMPurify from 'dompurify'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BBadge,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
  },
  data() {
    return {
      dataLoad: false,
      totalRows: '',
      search: '',
      activeSource: 'all',
      categorizedItems: {},
    }
  },
  computed: {
    availableSources() {
      return Object.keys(this.categorizedItems)
    },
    sourcesCount() {
      return this.availableSources.length
    },
    totalCertificates() {
      return this.availableSources.reduce(
        (acc, src) => acc + this.categorizedItems[src].length,
        0,
      )
    },
    uniqueEngineersCount() {
      const set = new Set()
      this.availableSources.forEach(src => {
        this.categorizedItems[src].forEach(item => {
          this.engineerList(item.engineer_names).forEach(n => set.add(n.toLowerCase()))
        })
      })
      return set.size
    },
    filteredGroups() {
      const term = this.search.trim().toLowerCase()
      const result = {}
      this.availableSources
        .filter(src => this.activeSource === 'all' || this.activeSource === src)
        .forEach(src => {
          const items = this.categorizedItems[src].filter(item => {
            if (!term) return true
            const en = (item.name && item.name.en ? item.name.en : '').toLowerCase()
            const ar = (item.name && item.name.ar ? item.name.ar : '').toLowerCase()
            const eng = (item.engineer_names || '').toLowerCase()
            return en.includes(term) || ar.includes(term) || eng.includes(term)
          })
          if (items.length) result[src] = items
        })
      return result
    },
    hasResults() {
      return Object.keys(this.filteredGroups).length > 0
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    sourceMeta(source) {
      const map = {
        SFDA: {
          icon: 'ShieldIcon', color: '#6e1fd4', soft: 'rgba(110,31,212,0.12)', gradient: 'linear-gradient(135deg, #7e2fe0, #4a1499)',
        },
        'SFDA-EG': {
          icon: 'ShieldIcon', color: '#5e35b1', soft: 'rgba(94,53,177,0.12)', gradient: 'linear-gradient(135deg, #7e57c2, #4527a0)',
        },
        Jeem: {
          icon: 'AwardIcon', color: '#c46a1a', soft: 'rgba(196,106,26,0.12)', gradient: 'linear-gradient(135deg, #f0972e, #b3661c)',
        },
        Saber: {
          icon: 'PackageIcon', color: '#8c5a78', soft: 'rgba(140,90,120,0.15)', gradient: 'linear-gradient(135deg, #a56b8e, #401b35)',
        },
        Moiat: {
          icon: 'SettingsIcon', color: '#e53935', soft: 'rgba(229,57,53,0.12)', gradient: 'linear-gradient(135deg, #ff4d4d, #b71c1c)',
        },
        SLS: {
          icon: 'CheckCircleIcon', color: '#2bb1ba', soft: 'rgba(43,177,186,0.14)', gradient: 'linear-gradient(135deg, #33c9d4, #1f8f97)',
        },
        GOEIC: {
          icon: 'GlobeIcon', color: '#d99406', soft: 'rgba(217,148,6,0.14)', gradient: 'linear-gradient(135deg, #f5b301, #d97e00)',
        },
        MOPH: {
          icon: 'ActivityIcon', color: '#1976d2', soft: 'rgba(25,118,210,0.12)', gradient: 'linear-gradient(135deg, #42a5f5, #1565c0)',
        },
        Halal: {
          icon: 'CheckSquareIcon', color: '#2e9e5b', soft: 'rgba(46,158,91,0.14)', gradient: 'linear-gradient(135deg, #43c46f, #217a44)',
        },
      }
      return map[source] || {
        icon: 'FileTextIcon',
        color: '#6c757d',
        soft: 'rgba(108,117,125,0.14)',
        gradient: 'linear-gradient(135deg, #8a94a6, #3b4252)',
      }
    },
    chipActiveStyle(source) {
      const meta = this.sourceMeta(source)
      return {
        background: meta.gradient,
        borderColor: 'transparent',
        color: '#fff',
        boxShadow: `0 6px 16px -6px ${meta.color}`,
      }
    },
    engineerList(names) {
      if (!names) return []
      return names
        .split(',')
        .map(n => n.trim())
        .filter(Boolean)
    },
    initials(name) {
      const parts = name.trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },
    avatarColor(name) {
      const palette = [
        'linear-gradient(135deg, #667eea, #764ba2)',
        'linear-gradient(135deg, #f093fb, #f5576c)',
        'linear-gradient(135deg, #4facfe, #00f2fe)',
        'linear-gradient(135deg, #43e97b, #38f9d7)',
        'linear-gradient(135deg, #fa709a, #fee140)',
        'linear-gradient(135deg, #30cfd0, #330867)',
        'linear-gradient(135deg, #a8edea, #fed6e3)',
        'linear-gradient(135deg, #ff9a9e, #fad0c4)',
        'linear-gradient(135deg, #f6d365, #fda085)',
        'linear-gradient(135deg, #5ee7df, #b490ca)',
      ]
      let hash = 0
      for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) % 1000000007
      }
      return palette[Math.abs(hash) % palette.length]
    },
    formatEngineerNames(names) {
      const clean = DOMPurify.sanitize(names, { ALLOWED_TAGS: [] })
      const list = clean.split(',').map(n => n.trim()).filter(Boolean)
      return `
        <div style="max-height: 55vh; overflow-y: auto; padding: 4px;">
          <table style="width: 100%; border-collapse: collapse; margin: auto;">
            <thead>
              <tr style="position: sticky; top: 0; background: #f3f2f7;">
                <th style="border-bottom: 2px solid #e0e0e0; padding: 10px; text-align: center; width: 48px;">#</th>
                <th style="border-bottom: 2px solid #e0e0e0; padding: 10px; text-align: left;">Engineer Name</th>
              </tr>
            </thead>
            <tbody>
              ${list.map((name, index) => `
                <tr style="background: ${index % 2 === 0 ? '#ffffff' : '#fafafa'};">
                  <td style="border-bottom: 1px solid #eee; padding: 8px 10px; text-align: center; color: #7367f0; font-weight: 600;">${index + 1}</td>
                  <td style="border-bottom: 1px solid #eee; padding: 8px 10px; text-align: left;">${name}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>
      `
    },
    ApprovalRow(names, certName) {
      if (!this.engineerList(names).length) return
      const getName = String.raw`${names}`
      this.$swal({
        title: certName ? `Engineers · ${certName}` : 'Engineer List',
        html: this.formatEngineerNames(getName),
        width: 620,
        showCancelButton: false,
        confirmButtonColor: '#7367f0',
        confirmButtonText: 'Close',
      })
    },
    async getData() {
      this.dataLoad = true
      try {
        const response = await axios.get('certificate_group')

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data[Object.keys(responseData?.data)[0]] || []
          const sources = [...new Set(data.map(item => item.source))]

          const grouped = {}
          sources.forEach(source => {
            grouped[source] = data.filter(item => item.source === source)
          })
          this.categorizedItems = grouped
          this.totalRows = responseData.data.count || data.length
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.main-certificate-page {
  --radius: 14px;
}

/* ===== Hero ===== */
.hero-card {
  border: none;
  border-radius: var(--radius);
  background: linear-gradient(120deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  box-shadow: 0 12px 30px -12px rgba(115, 103, 240, 0.6);
}
.hero-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 1.75rem 2rem;
  position: relative;
}
.hero-inner::after {
  content: '';
  position: absolute;
  right: -40px;
  top: -60px;
  width: 220px;
  height: 220px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  pointer-events: none;
}
.hero-text { max-width: 560px; z-index: 1; }
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  padding: 4px 12px;
  border-radius: 30px;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}
.hero-title {
  color: #fff;
  font-weight: 700;
  margin-bottom: 0.35rem;
}
.hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0;
  font-size: 0.9rem;
}
.hero-stats {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  z-index: 1;
}
.stat-pill {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  min-width: 132px;
}
.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  color: #7367f0;
}
.stat-value { font-size: 1.35rem; font-weight: 700; line-height: 1; }
.stat-label { font-size: 0.75rem; opacity: 0.9; }

/* ===== Toolbar ===== */
.toolbar-card { border-radius: var(--radius); }
.toolbar-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}
.search-group { max-width: 360px; flex: 1 1 260px; }
.clear-search { cursor: pointer; }
.source-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.source-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #e4e4ec;
  background: #fff;
  color: #6e6b7b;
  border-radius: 30px;
  padding: 5px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.source-chip:hover { border-color: #7367f0; color: #7367f0; }
.source-chip--active {
  background: #7367f0;
  border-color: #7367f0;
  color: #fff;
}

/* ===== Group ===== */
.groups-wrapper { display: flex; flex-direction: column; gap: 0.75rem; }
.source-group { animation: fadeUp 0.4s ease both; }
.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 8px 20px -12px rgba(0, 0, 0, 0.5);
}
.group-header-left { display: flex; align-items: center; gap: 0.85rem; }
.group-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.group-title { color: #fff; margin: 0; font-weight: 700; font-size: 1.15rem; }
.group-sub { font-size: 0.75rem; opacity: 0.85; }
.group-count {
  background: rgba(255, 255, 255, 0.25) !important;
  color: #fff !important;
  font-weight: 600;
  padding: 0.5rem 0.9rem;
}

/* ===== Card ===== */
.cert-card {
  position: relative;
  background: #fff;
  border-radius: var(--radius);
  box-shadow: 0 6px 18px -10px rgba(34, 41, 47, 0.25);
  overflow: hidden;
  height: calc(100% - 0.75rem);
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #f0f0f4;
}
.cert-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 34px -14px rgba(34, 41, 47, 0.35);
}
.cert-accent { height: 5px; width: 100%; }
.cert-body { padding: 1.1rem 1.25rem 0.75rem; flex: 1; }
.cert-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.cert-source-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cert-id { font-weight: 600; }
.cert-name-en {
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 0.15rem;
  color: #5e5873;
  min-height: 2.4rem;
}
.cert-name-ar {
  color: #a5a3b3;
  font-size: 0.9rem;
  margin-bottom: 0.9rem;
}

/* Engineers */
.cert-engineers {
  border-top: 1px dashed #ececf2;
  padding-top: 0.75rem;
}
.cert-engineers-head {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.6rem;
}
.avatar-stack { display: flex; align-items: center; cursor: pointer; }
.avatar-chip {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  border: 2px solid #fff;
  margin-left: -8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease;
}
.avatar-chip:first-child { margin-left: 0; }
.avatar-stack:hover .avatar-chip { transform: translateY(-2px); }
.avatar-more {
  background: #ebe9f1 !important;
  color: #6e6b7b !important;
}
.no-engineers {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #b9b9c3;
  font-style: italic;
}

/* Footer */
.cert-footer {
  display: flex;
  gap: 0.55rem;
  padding: 0.75rem 1.25rem 1rem;
}
.footer-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}
.footer-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}
.footer-btn--ghost {
  background: transparent;
  border: 1.5px solid;
}
.footer-btn--ghost:not(:disabled):hover {
  background: rgba(0, 0, 0, 0.03);
}
.footer-btn--solid {
  border: none;
  color: #fff;
  box-shadow: 0 4px 12px -4px rgba(34, 41, 47, 0.35);
}
.footer-btn--solid:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px -6px rgba(34, 41, 47, 0.4);
  filter: brightness(1.05);
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 767px) {
  .hero-inner { padding: 1.25rem; }
  .hero-stats { width: 100%; }
  .stat-pill { flex: 1 1 auto; }
}
</style>
